import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface NetworkState {
  isOnline: boolean;
  type?: string;
}

@Injectable({ providedIn: 'root' })
export class CapacitorService {
  private capacitor: any;
  private isCapacitorAvailable$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initializeCapacitor();
  }

  private initializeCapacitor(): void {
    try {
      if (typeof window !== 'undefined') {
        if ((window as any).Capacitor) {
          this.capacitor = (window as any).Capacitor;
          this.isCapacitorAvailable$.next(true);
        } else if ((window as any).capacitor) {
          this.capacitor = (window as any).capacitor;
          this.isCapacitorAvailable$.next(true);
        } else {
          this.isCapacitorAvailable$.next(false);
        }
      }
    } catch (e) {
      console.warn('Capacitor not available - running in web mode');
      this.isCapacitorAvailable$.next(false);
    }
  }

  isNativeApp(): boolean {
    // Verwende die native Capacitor-Methode isNativePlatform() für zuverlässige Erkennung
    // Dies gibt nur true zurück, wenn die App wirklich auf einem nativen Gerät (Android/iOS) läuft
    // Im Web-Browser (auch mit kleinem Fenster/Responsive Mode) gibt es false zurück
    if (!this.isCapacitorAvailable$.getValue()) {
      return false;
    }

    try {
      // Capacitor.isNativePlatform() ist die zuverlässigste Methode
      if (this.capacitor && typeof this.capacitor.isNativePlatform === 'function') {
        return this.capacitor.isNativePlatform();
      }

      // Fallback: Prüfe ob getPlatform() aus den Capacitor-Plugins kommt (nicht User-Agent)
      const platform = this.capacitor?.getPlatform?.();
      return platform === 'android' || platform === 'ios';
    } catch (e) {
      console.warn('Error checking native platform:', e);
      return false;
    }
  }

  async getDeviceInfo(): Promise<any> {
    if (!this.isNativeApp()) {
      return {
        platform: 'web',
        uuid: 'web-' + Math.random().toString(36).substr(2, 9)
      };
    }

    try {
      const Device = this.capacitor?.Device;
      if (Device) {
        return await Device.getInfo();
      }
    } catch (e) {
      console.error('Error getting device info:', e);
    }

    return null;
  }

  async checkNetworkStatus(): Promise<{ isOnline: boolean; type?: string }> {
    if (!this.isNativeApp()) {
      return { isOnline: this.getNavigatorOnline() };
    }

    try {
      const Network = this.capacitor?.Network;
      if (Network) {
        return await Network.getStatus();
      }
    } catch (e) {
      console.error('Error checking network status:', e);
    }

    return { isOnline: this.getNavigatorOnline() };
  }

  async writeFile(path: string, data: string): Promise<void> {
    if (!this.isNativeApp()) {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(path, data);
      return;
    }

    try {
      const Filesystem = this.capacitor?.Filesystem;
      if (Filesystem) {
        await Filesystem.writeFile({
          path,
          data,
          directory: 'Documents',
          encoding: 'utf8'
        });
      }
    } catch (e) {
      console.error('Error writing file:', e);
      throw e;
    }
  }

  async readFile(path: string): Promise<string | null> {
    if (!this.isNativeApp()) {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(path);
    }

    try {
      const Filesystem = this.capacitor?.Filesystem;
      if (Filesystem) {
        const result = await Filesystem.readFile({
          path,
          directory: 'Documents',
          encoding: 'utf8'
        });
        return result.data as string;
      }
      return null;
    } catch (e) {
      console.error('Error reading file:', e);
      return null;
    }
  }

  async deleteFile(path: string): Promise<void> {
    if (!this.isNativeApp()) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(path);
      }
      return;
    }

    try {
      const Filesystem = this.capacitor?.Filesystem;
      if (Filesystem) {
        await Filesystem.deleteFile({
          path,
          directory: 'Documents'
        });
      }
    } catch (e) {
      console.error('Error deleting file:', e);
      throw e;
    }
  }

  getNetworkStatusObservable(): Observable<{ isOnline: boolean }> {
    return new Observable((observer) => {
      const hasWindow = typeof window !== 'undefined';
      const isOnline = this.getNavigatorOnline();
      observer.next({ isOnline });

      const handleOnline = () => observer.next({ isOnline: true });
      const handleOffline = () => observer.next({ isOnline: false });

      if (hasWindow) {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
      }

      if (this.isNativeApp()) {
        try {
          const Network = this.capacitor?.Network;
          if (Network) {
            Network.addListener('networkStatusChange', (status: any) => {
              observer.next({ isOnline: status.connected });
            });
          }
        } catch (e) {
          console.warn('Could not setup native network listener:', e);
        }
      }

      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        }
      };
    });
  }

  getPlatform(): 'ios' | 'android' | 'web' {
    // Verwende die Capacitor-API getPlatform() für zuverlässige Plattform-Erkennung
    // Dies ist besser als User-Agent-Prüfung, da es nur in nativen Apps funktioniert
    if (!this.isCapacitorAvailable$.getValue()) {
      return 'web';
    }

    try {
      // Capacitor.getPlatform() gibt die echte Plattform zurück (nicht basierend auf User-Agent)
      if (this.capacitor && typeof this.capacitor.getPlatform === 'function') {
        const platform = this.capacitor.getPlatform();
        if (platform === 'ios' || platform === 'android') {
          return platform;
        }
      }
    } catch (e) {
      console.warn('Error getting platform from Capacitor:', e);
    }

    return 'web';
  }

  private getNavigatorOnline(): boolean {
    if (typeof navigator === 'undefined') {
      return true;
    }
    return navigator.onLine;
  }
}
